import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { db, auth } from "../Firebase";
import {
  doc,
  getDoc,
  query,
  where,
  collection,
  getDocs,
  onSnapshot,
  setDoc,
  updateDoc,
  increment,
  serverTimestamp,
  deleteDoc
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import "../styles/MyCourseDetails.css";
import { toast } from "react-toastify";

/* ================= FAQ ITEM ================= */
const FAQItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className={`faq-item ${open ? "open" : ""}`}>
      <div
        className="faq-question"
        onClick={() => setOpen(!open)}
      >
        <span>{question}</span>
        <span className="faq-arrow">
          {open ? "⌃" : "⌄"}
        </span>
      </div>

      {open && (
        <div className="faq-answer">
          <p>{answer}</p>
        </div>
      )}
    </div>
  );
};

const MyCourseDetails = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();

  const [course, setCourse] = useState(null);

  const [progress, setProgress] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [lastVideoIndex, setLastVideoIndex] = useState(0);

  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [alreadyReviewed, setAlreadyReviewed] = useState(false);
  const [existingReview, setExistingReview] = useState(null);
  const [submittingReview, setSubmittingReview] = useState(false);

  const [loading, setLoading] = useState(true);

  const [certificateApplied, setCertificateApplied] = useState(false);
  const [checkingCertificate, setCheckingCertificate] = useState(true);


  /* ================= FETCH DATA ================= */
  useEffect(() => {
    if (!courseId) {
      navigate("/myCourses");
      return;
    }

    let unsubTiming = null;

    const unsubAuth = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate("/login");
        return;
      }

      try {
        /* ---------- CHECK PURCHASE ---------- */
        const orderSnap = await getDocs(
          query(
            collection(db, "orders"),
            where("userId", "==", user.uid),
            where("courseId", "==", courseId)
          )
        );

        if (orderSnap.empty) {
          navigate(`/courseDetails/${courseId}`);
          return;
        }

        /* ---------- EXISTING REVIEW ---------- */
        const reviewRef = doc(db, "reviews", `${courseId}_${user.uid}`);
        const reviewSnap = await getDoc(reviewRef);

        if (reviewSnap.exists()) {
          const r = reviewSnap.data();
          setAlreadyReviewed(true);
          setExistingReview(r);
          setRating(r.rating);
          setReviewText(r.review);
        }

        /* ---------- LIVE PROGRESS ---------- */
        const timingRef = doc(
          db,
          "courseTiming",
          `${user.uid}_${courseId}`
        );

        unsubTiming = onSnapshot(timingRef, (snap) => {
          if (!snap.exists()) return;

          const t = snap.data();

          setProgress(
            Math.min(100, Math.max(0, t.progressPercentage || 0))
          );
          setCompleted(!!t.completed);
          setLastVideoIndex(
            typeof t.lastVideoIndex === "number"
              ? t.lastVideoIndex
              : 0
          );
        });

        /* ---------- COURSE DATA ---------- */
        const courseSnap = await getDoc(doc(db, "courses", courseId));

        if (!courseSnap.exists()) {
          navigate("/myCourses");
          return;
        }

        const data = courseSnap.data();

        setCourse({
          id: courseSnap.id,
          title: data.title || "",
          description: data.description || "",
          duration: data.duration || "Self-paced",
          thumbnail: data.thumbnail || "",
          previewVideo: data.previewVideo || "",
          faqs: Array.isArray(data.faqs) ? data.faqs : [],
          videos: Array.isArray(data.videos)
            ? data.videos.filter(v => !v.isSample)
            : [],
          instructorId: data.instructorId // ✅ IMPORTANT
        });


        /* ---------- CHECK CERTIFICATE ---------- */
          const certRef = doc(db, "certificates", `${courseId}_${user.uid}`);
          const certSnap = await getDoc(certRef);

          if (certSnap.exists()) {
            setCertificateApplied(true);
          }
          setCheckingCertificate(false);



        setLoading(false);
      } catch (err) {
        console.error(err);
        navigate("/myCourses");
      }
    });

    return () => {
      unsubAuth();
      if (unsubTiming) unsubTiming();
    };
  }, [courseId, navigate]);


  /* ================= SUBMIT REVIEW ================= */
  const submitReview = async () => {
    if (!rating || alreadyReviewed) return;

    try {
      setSubmittingReview(true);

      const user = auth.currentUser;
      if (!user) return;

      const reviewRef = doc(db, "reviews", `${courseId}_${user.uid}`);
      const summaryRef = doc(db, "courseReviewsSummary", courseId);

      /* ---------- SAVE REVIEW (ONE TIME) ---------- */
      await setDoc(reviewRef, {
        courseId,
        userId: user.uid,
        rating,
        review: reviewText,
        createdAt: serverTimestamp()
      });

      /* ---------- UPDATE SUMMARY ---------- */
      const summarySnap = await getDoc(summaryRef);

      if (!summarySnap.exists()) {
        // First review for this course
        await setDoc(summaryRef, {
          courseId,
          totalReviews: 1,
          averageRating: rating,
          stars: {
            1: rating === 1 ? 1 : 0,
            2: rating === 2 ? 1 : 0,
            3: rating === 3 ? 1 : 0,
            4: rating === 4 ? 1 : 0,
            5: rating === 5 ? 1 : 0
          }
        });
      } else {
        const data = summarySnap.data();
        const newTotal = data.totalReviews + 1;

        const newAvg =
          (data.averageRating * data.totalReviews + rating) /
          newTotal;

        await updateDoc(summaryRef, {
          totalReviews: increment(1),
          averageRating: Number(newAvg.toFixed(1)),
          [`stars.${rating}`]: increment(1)
        });
      }

      setAlreadyReviewed(true);
      setExistingReview({ rating, review: reviewText });
      setRating(0);
      setReviewText("");

      toast.success("Thank you for your review ⭐");
    } catch (err) {
      console.error(err);
      toast.error("Failed to submit review");
    } finally {
      setSubmittingReview(false);
    }
  };

  /* ================= DELETE REVIEW ================= */
  const deleteReview = async () => {
    try {
      const user = auth.currentUser;
      if (!user || !existingReview) return;

      const reviewRef = doc(db, "reviews", `${courseId}_${user.uid}`);
      const summaryRef = doc(db, "courseReviewsSummary", courseId);

      const summarySnap = await getDoc(summaryRef);
      if (!summarySnap.exists()) return;

      const data = summarySnap.data();
      const newTotal = data.totalReviews - 1;

      const newAvg =
        newTotal === 0
          ? 0
          : (data.averageRating * data.totalReviews -
              existingReview.rating) /
            newTotal;

      /* ---------- UPDATE SUMMARY ---------- */
      await updateDoc(summaryRef, {
        totalReviews: increment(-1),
        averageRating: Number(newAvg.toFixed(1)),
        [`stars.${existingReview.rating}`]: increment(-1)
      });

      /* ---------- DELETE REVIEW ---------- */
      await deleteDoc(reviewRef);

      setAlreadyReviewed(false);
      setExistingReview(null);
      setRating(0);
      setReviewText("");

      toast.success("Review deleted successfully");
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete review");
    }
  };


  const generateCertificateNumber = (courseTitle) => {
    const SITE = "VIDIFY";

    const courseCode = (courseTitle || "COURSE")
      .replace(/[^A-Za-z]/g, "")
      .substring(0, 4)
      .toUpperCase();

    const random = Math.floor(10000 + Math.random() * 90000);

    return `${SITE}-${courseCode}-${random}`;
  };


    const handleApplyCertificate = async () => {
  try {
    const user = auth.currentUser;
    if (!user || !course) return;

    const certRef = doc(db, "certificates", `${courseId}_${user.uid}`);
    const certSnap = await getDoc(certRef);

    // Already created
    if (certSnap.exists()) {
      setCertificateApplied(true);
      return;
    }

    /* ---------- STUDENT ---------- */
    const studentSnap = await getDoc(doc(db, "users", user.uid));
    if (!studentSnap.exists()) {
      toast.error("Student data missing");
      return;
    }
    const student = studentSnap.data();

    /* ---------- TEACHER ---------- */
    const teacherSnap = await getDoc(
      doc(db, "users", course.instructorId)
    );
    if (!teacherSnap.exists()) {
      toast.error("Instructor data missing");
      return;
    }
    const teacher = teacherSnap.data();

    /* ---------- CREATE CERTIFICATE ---------- */
    await setDoc(certRef, {
      certificateNumber: generateCertificateNumber(course.title),

      courseId,
      userId: user.uid,
      instructorId: course.instructorId,

      studentName:
        student.certificateName ||
        student.fullName ||
        "Student",

      teacherName:
        teacher.fullName || "Instructor",

      teacherSignature:
        teacher.signatureURL || "",

      courseName: course.title,
      issuedAt: serverTimestamp()
    });

    setCertificateApplied(true);
    toast.success("Certificate applied successfully 🎓");

  } catch (err) {
    console.error(err);
    toast.error("Failed to apply for certificate");
  }
};



  if (loading) {
    return <p align="center">Loading course…</p>;
  }

  return (
    <div className="course-details-page">
      {/* LEFT */}
      <div className="course-left">
        <h1 className="course-title">{course.title}</h1>

        <p className="course-desc">{course.description}</p>

        {/* PROGRESS */}
        <div className="progress-box">
          <div className="progress-header">
            <span>Course Progress</span>
            <span>{progress}%</span>
          </div>

          <div className="progress-track">
            <div
              className="progress-fill"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* SAMPLE VIDEO */}
        {course.previewVideo && (
          <>
            <h2 className="content-title">Sample Video</h2>

            <div className="video-card">
              <video controls poster={course.thumbnail}>
                <source
                  src={course.previewVideo}
                  type="video/mp4"
                />
              </video>

              <div className="video-meta">
                <h4>Course Preview</h4>
                <span className="video-tag sample">
                  Sample Video
                </span>
              </div>
            </div>
          </>
        )}

        {/* COURSE VIDEOS */}
        <h2 className="content-title">Course Videos</h2>

        {course.videos.length > 0 ? (
          <div className="video-stack">
            {course.videos.map((v, i) => (
              <div
                key={v.id}
                className={`video-card ${
                  i === lastVideoIndex ? "active" : ""
                }`}
              >
                <div
                  className="locked-video"
                  onClick={() =>
                    window.open(
                      `/coursePlayer/${course.id}?v=${lastVideoIndex}`,
                      "_blank"
                    )
                  }
                >
                  <video poster={course.thumbnail}>
                    <source src={v.url} type="video/mp4" />
                  </video>

                  <div className="video-lock-overlay">
                    🔒 Watch in player
                  </div>
                </div>

                <div className="video-meta">
                  <h4>
                    {i + 1}. {v.title || "Untitled Video"}
                  </h4>

                  <p className="video-desc">
                    {v.description || "No description"}
                  </p>

                  <span className="video-tag">
                    Course Video
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ color: "#6b7280" }}>
            Course videos will be added soon.
          </p>
        )}

        {/* FAQ */}
        <h2 className="faq-title">
          Frequently Asked Questions
        </h2>

        <div className="faq">
          {course.faqs.length > 0 ? (
            course.faqs.map((f, i) => (
              <FAQItem
                key={i}
                question={f.q}
                answer={f.a}
              />
            ))
          ) : (
            <FAQItem
              question="Will I get a certificate?"
              answer="Yes, after completing the course."
            />
          )}
        </div>

        {/* ================= REVIEW SECTION ================= */}
        {completed && (
          <div className="review-box">
            <h2 className="content-title">Course Review</h2>

            {/* ---------- IF REVIEW NOT SUBMITTED ---------- */}
            {!alreadyReviewed && (
              <>
                {/* STAR RATING */}
                <div className="star-row">
                  {[1, 2, 3, 4, 5].map(star => (
                    <span
                      key={star}
                      className={`star ${
                        rating >= star ? "active" : ""
                      }`}
                      onClick={() => setRating(star)}
                    >
                      ★
                    </span>
                  ))}
                </div>

                {/* REVIEW TEXT */}
                <textarea
                  placeholder="Write your review..."
                  value={reviewText}
                  onChange={e => setReviewText(e.target.value)}
                />

                <button
                  className="btn-primary"
                  disabled={
                    rating === 0 ||
                    submittingReview ||
                    alreadyReviewed
                  }
                  onClick={submitReview}
                >
                  {submittingReview
                    ? "Submitting..."
                    : "Submit Review"}
                </button>
              </>
            )}

            {/* ---------- IF REVIEW ALREADY SUBMITTED ---------- */}
            {alreadyReviewed && existingReview && (
              <div className="submitted-review">
                {/* STARS DISPLAY */}
                <div className="star-row readonly">
                  {[1, 2, 3, 4, 5].map(star => (
                    <span
                      key={star}
                      className={`star ${
                        existingReview.rating >= star
                          ? "active"
                          : ""
                      }`}
                    >
                      ★
                    </span>
                  ))}
                </div>

                {/* REVIEW TEXT */}
                <p className="review-text">
                  {existingReview.review || "No review text"}
                </p>

                {/* DELETE BUTTON */}
                <button
                  className="btn-danger"
                  onClick={deleteReview}
                >
                  Delete Review
                </button>
              </div>
            )}
          </div>
        )}

      </div>

      {/* RIGHT */}
      <div className="course-right">
        <div className="course-card">
          <p className="course-duration">
            ⏱ Duration: {course.duration}
          </p>

          <div className="certificate-box">
            {completed ? (
              checkingCertificate ? (
                <p>Checking certificate…</p>
              ) : certificateApplied ? (
                <>
                  <h3>🎓 Certificate Ready</h3>
                  <button
                    className="certificate-btn"
                    onClick={() => navigate(`/certificate/${courseId}`)}
                  >
                    Download Certificate
                  </button>
                </>
              ) : (
                <>
                  <h3>🎓 Course Completed</h3>
                  <button
                    className="certificate-btn apply"
                    onClick={handleApplyCertificate}
                  >
                    Apply for Certificate
                  </button>
                </>
              )
            ) : (
              <>
                <h3>Certificate Locked</h3>
                <p>Complete {100 - progress}% more</p>
              </>
            )}
          </div>



          <button
            className="btn-primary"
            onClick={() =>
              window.open(
                `/coursePlayer/${course.id}?v=${lastVideoIndex}`,
                "_blank"
              )
            }
          >
            Continue Learning
          </button>
        </div>
      </div>
    </div>
  );
};

export default MyCourseDetails;
